import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, LinearProgress, Typography } from '@mui/material';
import { DropzoneArea } from 'material-ui-dropzone';
import { read, utils } from 'xlsx';
import './import.css';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from 'src/store/RootReducer';
import { AppActions } from 'src/store/AppReducer';
import SnackBar from 'src/components/SnackBar/SnackBar';
import FileSummary from './FileSummary';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import ProjectSelect from 'src/content/taskManagement/common/ProjectSelect';
import ImportSummaryModal from './ImportSummaryModal';
import { importTimeEntries } from 'src/services/timeManagementService';
import { CircularProgress } from '@material-ui/core';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

const LoadTimeEntry = ({ toggle, projectList }) => {
  const dispatch = useDispatch();
  const appUser = useSelector((state: RootStateType) => state.appReducer?.user);
  const [project, setProject] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const [fileName, setFileName] = useState('');
  const [errors, setErrors] = useState('');
  const [fileTemplateStatus, setFileTemplateStatus] = useState(false);
  const [tableValues, setTableValues] = useState<any>([]);
  const [headers] = useState([
    'Team Member',
    'Ticket Key',
    'Ticket Summary',
    'Date',
    'Time Spent',
    'Time Spent (Hours)',
    'Comment',
    'Activity Type'
  ]);

  const [requiredFields] = useState([
    'Team Member',
    'Ticket Key',
    'Ticket Summary',
    'Date',
    'Time Spent (Hours)',
    'Comment',
    'Activity Type'
  ]);

  const [isDisabled, setIsDisabled] = useState(true);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const [attachment, setAttachment] = useState<any>({});
  const [progress, setProgress] = useState(0);

  const [successRecords, setSuccessRecords] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [invalidValues, setInvalidValues] = useState([]);
  const [invalidRows, setInvalidRows] = useState(0);

  const [openIssues, setOpenIssues] = useState(false);
  const [importSummaryReport, setImportSummaryReport] = useState<any>({});

  const onImport = () => {
    setIsLoading(true);
    if (attachment) {
      const payload = {
        file: attachment.file,
        projectId: project
      }

      importTimeEntries(payload).then((res) => {
        const data = res.data;

        if(data?.statusCode !== 200){
          setSnackBarMessage(data?.statusCode ? data?.message : 'Unable to import time entry');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
        } else {
          console.log(data?.data)
          if(data?.data?.success && data?.data?.failedCount === 0){
            setSnackBarMessage('Your file has been imported successfully.');
            setSnackbarSeverity('success');
            setSnackBarOpen(true);
          } else{
            setSuccessRecords(data?.data?.successCount);
            setTotalRecords(tableValues.length);
            setImportSummaryReport({
              successCount: data?.data?.successCount,
              failedCount: data?.data?.failedCount,
              totalCount: data?.data?.successCount + data?.data?.failedCount,
              errors: data?.data?.errors
            })
          }
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        dispatch(AppActions.hideLoader());
        setSnackBarMessage(error?.request?.response ? error?.request?.response : 'Unable to import');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
    }
  };

  const handleFileChange = (files) => {
    setTableValues([]);
    setFileTemplateStatus(false);
    setProgress(0);
    setIsDisabled(true);

    const file = files?.[0];
    if (!file) return;

    const allowedExtensions = ["xlsx", "xls"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      setErrors("The file you selected is not supported. Please ensure yout file ends in .xlsx and .xls and try again.");
      return;
    }

    if (files[0]) {
      setAttachment({ file: files[0] });
      setFileName(files[0].name);
      const reader = new FileReader();
      if (reader.readAsBinaryString) {
        reader.onload = (e) => {
          const workbook = read(reader.result, { type: 'binary' });
          setProgress(10);
          setIsDisabled(true);
          const headerSheets = workbook.Sheets[workbook.SheetNames[0]];
          const excelHeaders: any = utils.sheet_to_json(headerSheets, {
            header: 1
          });

          const firstSheet = workbook.SheetNames[0];
          const excelRows = utils.sheet_to_json(workbook.Sheets[firstSheet]);

          if (
            !excelHeaders[0]?.every((value, index) => value === headers[index])
          ) {
            setFileTemplateStatus(false);
            setErrors('File template is invalid');
          } else if(excelRows.length === 0){
            setFileTemplateStatus(false);
            setErrors('The file your uploading is empty / doesn’t have any valid records');
          } else if(!project){
            setFileTemplateStatus(false);
            setErrors('Please select the project.');
          } else {
            setErrors('');
            setTableValues(excelRows);
            setFileTemplateStatus(true);
          }
        };
        setProgress(20);
        setIsDisabled(true);
        reader.readAsBinaryString(files[0]);
      }
    }
  };

  useEffect(() => {
    if (fileTemplateStatus) {
      setErrors('');
    }
  }, [fileTemplateStatus]);

  useEffect(() => {
    setProgress(0);
    setIsDisabled(true);
  }, [fileName]);

  const toggleIssues = () => {
    setOpenIssues(!openIssues);
  };

  useEffect(() => {
    setSuccessRecords(tableValues.length - invalidRows);
    setTotalRecords(tableValues.length);
  }, [tableValues, invalidRows]);

  useEffect(() => {
    if (tableValues.length > 0 && fileTemplateStatus) {
      let emptyRowArr = [];
      //let invalidRow = 0;
      tableValues.forEach((tab) => {
        const rowNum = tab.__rowNum__;
        setProgress(40);
        setIsDisabled(true);
        const entries = Object.entries(tab);
        const emptyFields = requiredFields.filter((head) => {
          if (!entries.some((val) => val[0] === head)) return head;
        });

        const emptyFieldsRows = emptyFields.map((field) => {
          return {
            error: `${field} is Required`,
            row: rowNum
          };
        });

        if (emptyFieldsRows.length > 0) {
          emptyRowArr = [...emptyRowArr, ...emptyFieldsRows];
        }
        setTimeout(() => {
          setProgress(70);
        }, 500);
        setIsDisabled(true);
      });

      setTimeout(() => {
        setProgress(100);
      }, 1000);
      setIsDisabled(false);
      setInvalidValues([...emptyRowArr]);
      //setInvalidRows(invalidRow);
    }
  }, [tableValues, requiredFields, fileTemplateStatus]);

  return (
    <>
      <Grid sx={{ px: 3, mt: '0.3rem' }} container spacing={2} className='import-modal-form'>
        <Grid item xs={12} display="flex" alignItems="center" justifyContent='space-between'>          
          <Typography
            style={{
              color: '#223354',
              fontWeight: '700',
              fontSize: '15px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
            }}
            sx={{ pb: 1 }}
            variant="h4"
          >
            Load from XLS
          </Typography>
          <Grid item xs={10}>
            <div className='time-entry-load-file' style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'end'
            }}>
              <div>
                <ProjectSelect
                  isLabel={false}
                  width='250px'
                  list={projectList}
                  value={project}
                  showDefaultProject={false}
                  onChange={(e: any) => {
                    setProject(e.target.value);
                    setErrors('');
                  }}
                />
              </div>
              <div>
                <Button
                  id="btn-onSubmit"
                  sx={{
                    ml: 2,
                    fontSize: '13px',
                    fontWeight: 'normal',
                    background: '#0069E4',
                  }}
                  startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                  href={process.env.PUBLIC_URL + '/templates/time-entry-template.xlsx'}
                  variant="contained"
                  type="submit"
                >
                  Download Template
                </Button>
              </div>
            </div>
          </Grid>
        </Grid>
        {!fileTemplateStatus && (
          <Grid display="flex" alignItems="center" item xs={12}>
            <Grid 
              sx={{
                width: '100%',
                px: 2,
                py: 1.2,
                borderRadius: '0.3rem'
              }}
              bgcolor={'rgba(230, 233, 255, 0.5)'}
              display='flex'
              flexDirection='row'
              alignItems='center'
              gap={1}
            >
              <ErrorOutlinedIcon className='warning-icon'/>
              <Typography sx={{ color: '#5569ff' }} variant="body2">
                Please make sure to use the given template
              </Typography>
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box position="relative">
            <DropzoneArea
              onChange={(e) => handleFileChange(e)}
              showPreviews={false}
              showPreviewsInDropzone={false}
              filesLimit={1}
              showAlerts={false}
              dropzoneClass="custom-dropzone"
              dropzoneText=""
            />
            <Box className="upload-content">
              <UploadFileOutlinedIcon
                sx={{
                  color: '#6C63FF',
                  fontSize: 40,
                  mb: 1,
                }}
              />

              <Typography variant="body1">
                <span className="upload-link">Click to upload</span>
                {" "}or drag and drop
              </Typography>

              <Typography variant="body2" className="upload-subtext">
                Supported formats: .xlsx, .xls (Max 10MB)
              </Typography>
            </Box>
          </Box>
          {errors && (
            <Grid display="flex" alignItems="flex-start" item xs={12}>
              <Grid 
                sx={{
                  mt: 2,
                  width: '100%',
                  px: 2,
                  py: 1.2,
                  borderRadius: '0.3rem'
                }}
                bgcolor={'rgba(255, 230, 230, 0.5)'}
                display='flex'
                flexDirection='row'
                alignItems='center'
                gap={1}
              >
                <ErrorOutlinedIcon color='error'/>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <Typography sx={{ color: 'red' }} variant="body1" fontWeight='bold'>
                    {!errors.includes('Please select the project.') && 'Invalid File Type'}
                  </Typography>
                  <Typography sx={{ color: 'red' }} variant="body2">
                    {errors}
                  </Typography>
                </div>
              </Grid>
            </Grid>
          )}
        </Grid>
        {fileTemplateStatus && (
          <Grid item xs={12}>
            <img
              src={process.env.PUBLIC_URL + '/img/excelsheet.jpg'}
              alt=""
              style={{
                width: '48px',
                height: '48px'
              }}
            />
            <Box sx={{ width: '100%' }}>
              <Grid item display="flex" justifyContent="between">
                <p style={{ marginBottom: 0 }}>{fileName}</p>
                <p style={{ marginBottom: 0, marginLeft: 'auto' }}>
                  {progress}
                </p>
              </Grid>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          </Grid>
        )}
        {tableValues?.length > 0 && (
          <FileSummary
            tableValues={tableValues}
            successRecords={successRecords}
            totalRecords={totalRecords}
            toggleIssues={toggleIssues}
            invalidRows={invalidRows}
            errorsCount={totalRecords - successRecords || 0}
          />
        )}
        <Grid container spacing={2}>
          <Grid
            style={{
              marginTop: '2rem',
              marginBottom: '1rem'
            }}
            item
            sx={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}
            xs={12}
          >
            <Button
              id="btn-onClose"
              sx={{
                width: '120px',
                fontWeight: 'normal',
                background: '#F5F7FF',
                fontSize: '13px'
              }}
              onClick={() => toggle()}
            >
              Cancel
            </Button>
            <Button
              id="btn-onSubmit"
              sx={{
                ml: 2,
                width: '120px',
                fontSize: '13px',
                fontWeight: 'normal',
                backgroundColor: isDisabled ? '#eee' : '#0069E4',
                color: isDisabled ? 'rgba(34, 51, 84, 0.5)' : '#FFFFFF',
              }}
              startIcon={isLoading ? <CircularProgress size={15}/> : <></>}
              disabled={isDisabled || isLoading}
              variant="contained"
              onClick={() => onImport()}
            >
              Import
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
      <ImportSummaryModal
        importSummaryReport={importSummaryReport}
        onClose={toggleIssues}
        open={openIssues}
      />
    </>
  );
};

export default LoadTimeEntry;
